import { Integration, IntegrationSetting, CreateIntegrationDto, CreateIntegrationBackendDto, UpdateIntegrationBackendDto } from "../types/integration";
import { apiService } from "./api.service";
import { getSupabaseClient } from "../config/supabase";
import { STORAGE_BUCKET_NAME, getStorageUrl } from "../utils/utils";

const INTEGRATIONS_FOLDER = 'integrations';

const getIntegrationsPreviewUrl = (filename: string) => 
  getStorageUrl(`${INTEGRATIONS_FOLDER}/${filename}`);

export class IntegrationService {
  private static instance: IntegrationService;

  private constructor() {
    // Initialize service
  }

  static getInstance(): IntegrationService {
    if (!IntegrationService.instance) {
      IntegrationService.instance = new IntegrationService();
    }
    return IntegrationService.instance;
  }

  async getIntegrations(params?: {
    page?: number;
    pageSize?: number;
    category?: string;
    search?: string;
    enabled?: boolean;
  }): Promise<{ data: Integration[]; hasMore: boolean }> {
    // Pass pagination parameters to apiService.getIntegrations
    const response = await apiService.getIntegrations(params);
    return {
      data: response.data?.map(integration => ({
        ...integration,
        logo_url: integration.logo_url ? (integration.logo_url.startsWith('http') ? integration.logo_url : getIntegrationsPreviewUrl(integration.logo_url)) : ''
      })) || [],
      hasMore: response.hasMore // Assuming apiService.getIntegrations returns hasMore
    };
  }

  async getIntegration(id: string): Promise<Integration> {
    const data = await apiService.getIntegration(id);
    if (!data) throw new Error(`Integration not found: ${id}`);
    return {
      ...data,
      logo_url: data.logo_url ? (data.logo_url.startsWith('http') ? data.logo_url : getIntegrationsPreviewUrl(data.logo_url)) : ''
    };
  }

  async uploadIntegrationLogo(file: File): Promise<string> {
    const supabase = getSupabaseClient();
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `${INTEGRATIONS_FOLDER}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET_NAME)
      .upload(filePath, file);

    if (uploadError) {
      throw new Error(`Error uploading file: ${uploadError.message}`);
    }

    // Get public URL but we don't need to use it - we just return the filename
    supabase.storage
      .from(STORAGE_BUCKET_NAME)
      .getPublicUrl(filePath);

    return fileName; // Return only the filename
  }

  async createIntegration(
    integration: CreateIntegrationDto,
    logoFile?: File
  ): Promise<Integration> {
    let logo_url = integration.logo_url;
    
    if (logoFile) {
      logo_url = await this.uploadIntegrationLogo(logoFile);
    }

    const backendDto: CreateIntegrationBackendDto = {
      ...integration,
      display_order: 0, // Set default display order
      logo_url,
      settings_schema: integration.settings_schema 
        ? Array.isArray(integration.settings_schema)
          ? JSON.stringify(integration.settings_schema)
          : integration.settings_schema
        : '[]'
    };

    const data = await apiService.createIntegration(backendDto);
    if (!data) throw new Error("Failed to create integration");
    return {
      ...data,
      logo_url: data.logo_url ? getIntegrationsPreviewUrl(data.logo_url) : ''
    };
  }

  async updateIntegration(
    id: string,
    updates: Partial<Integration>,
    logoFile?: File
  ): Promise<Integration> {
    let logo_url = updates.logo_url;
    
    if (logoFile) {
      logo_url = await this.uploadIntegrationLogo(logoFile);
      updates.logo_url = logo_url;
    }

    // Ensure we don't modify display_order
    const { display_order, ...updateData } = updates;
    const backendUpdates: UpdateIntegrationBackendDto = {
      name: updateData.name,
      category: updateData.category,
      description: updateData.description,
      logo_url: updateData.logo_url,
      latency_impact: updateData.latency_impact,
      keywords: updateData.keywords,
      documentation: typeof updateData.documentation === 'object' 
        ? updateData.documentation 
        : updateData.documentation || undefined,
      enabled: updateData.enabled,
      destination: updateData.destination,
      sync_injector: updateData.sync_injector,
      settings_schema: updateData.settings_schema 
        ? Array.isArray(updateData.settings_schema)
          ? JSON.stringify(updateData.settings_schema)
          : updateData.settings_schema
        : undefined
    };

    const data = await apiService.updateIntegration(id, backendUpdates);
    if (!data) throw new Error(`Integration not found: ${id}`);
    return {
      ...data,
      logo_url: data.logo_url ? getIntegrationsPreviewUrl(data.logo_url) : ''
    };
  }

  async enableIntegration(id: string): Promise<Integration> {
    return this.updateIntegration(id, { enabled: true });
  }

  async disableIntegration(id: string): Promise<Integration> {
    return this.updateIntegration(id, { enabled: false });
  }

  async deleteIntegration(id: string): Promise<void> {
    return apiService.deleteIntegration(id);
  }

  async updateIntegrationSettings(
    id: string,
    settings: IntegrationSetting[] | Record<string, unknown>,
  ): Promise<Integration> {
    if (Array.isArray(settings)) {
      return this.updateIntegration(id, { settings_schema: settings });
    } else {
      // Convert Record<string, unknown> to IntegrationSetting[]
      const settingsArray = Object.entries(settings).map(([key, value]) => ({
        label: key,
        value: String(value),
        type: ['Destination'] as Array<'Destination' | 'Sync Injector'>
      }));
      return this.updateIntegration(id, { settings_schema: settingsArray });
    }
  }

  async getCategories(): Promise<string[]> {
    return apiService.getIntegrationCategories();
  }

  async validateSettings(
    id: string,
    settings: Record<string, unknown>,
  ): Promise<{ valid: boolean; errors?: Record<string, string> }> {
    return apiService.validateIntegrationSettings(id, settings);
  }

  async testConnection(id: string): Promise<{
    success: boolean;
    message?: string;
  }> {
    return apiService.testIntegrationConnection(id);
  }

  sortIntegrations(integrations: Integration[]): Integration[] {
    return [...integrations].sort((a, b) => {
      // First sort by display_order
      if (a.display_order !== b.display_order) {
        return a.display_order - b.display_order;
      }
      // Then by name
      return a.name.localeCompare(b.name);
    });
  }

  filterIntegrationsByKeywords(
    integrations: Integration[],
    keywords: string[],
  ): Integration[] {
    if (!keywords.length) return integrations;

    const normalizedKeywords = keywords.map((k) => k.toLowerCase());
    return integrations.filter((integration) =>
      normalizedKeywords.some((keyword) =>
        integration.keywords?.some((k) => k.toLowerCase().includes(keyword)) ?? false,
      ),
    );
  }
}

export const integrationService = IntegrationService.getInstance();
